import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Modal, ScrollView } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import i18n from 'i18n-js';

const CameraSelectModal = ({
  visible,
  cameras,
  onSelect,
  onCancel,
  step,
  onNext,
  onBack,
  showBackButton,
  selectedCameraId,
}) => {
  const backCameras = cameras.filter(camera => {
    const label = (camera.label || '').toLowerCase();
    return !label.includes('front');
  });

  const getCameraLabel = (camera, index) => {
    if (camera.label && camera.label.trim() !== '') {
      return camera.label;
    }
    return `Camera ${index + 1}`;
  };

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="fade"
      onRequestClose={onCancel}
    >
      <View style={styles.overlay}>
        <LinearGradient
          colors={['#d47d02', '#ffb636']}
          start={{ x: 0, y: 1 }}
          end={{ x: 0, y: 0 }}
          style={styles.modalContainer}
        >
          {step === 1 ? (
            <>
              {/* Header row — centered title with info icon, no close button */}
              <View style={styles.headerRow}>
                <View style={styles.sidePlaceholder} />
                <View style={styles.titleWithIcon}>
                  <Ionicons name="information-circle" size={20} color="#4b4132" style={{ marginRight: 6 }} />
                  <Text style={styles.title}>Choose the right camera</Text>
                </View>
                <View style={styles.sidePlaceholder} />
              </View>

              <Text style={styles.instructionBody}>
                On the next screen you will see a list of cameras available on your device.
              </Text>

              {/* Tip box */}
              <View style={styles.tipBox}>
                <Ionicons name="bulb-outline" size={18} color="#7a5c00" style={{ marginRight: 8, marginTop: 2 }} />
                <Text style={styles.tipBoxText}>
                  Choose the <Text style={styles.bold}>largest array</Text> of back-facing cameras.{'\n'}
                  Choose <Text style={styles.bold}>"Three Camera Back"</Text> over <Text style={styles.bold}>"Dual Camera Back"</Text>
                </Text>
              </View>

              <Text style={styles.tipFooter}>
                This will give your device maximum flexibility to switch between cameras during scanning.
              </Text>

              {/* Cancel + Next buttons */}
              <View style={styles.buttonRow}>
                <TouchableOpacity
                  style={styles.step1CancelButton}
                  onPress={onCancel}
                  testID="cancelButton"
                >
                  <Text style={styles.cancelButtonText}>
                    {i18n.t('camScanner.cancel', { defaultValue: 'Cancel' })}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.nextButton}
                  onPress={onNext}
                  testID="nextButton"
                >
                  <Text style={styles.nextButtonText}>
                    {i18n.t('camScanner.next', { defaultValue: 'Next' })}
                  </Text>
                  <Ionicons name="arrow-forward" size={18} color="#fff" style={{ marginLeft: 6 }} />
                </TouchableOpacity>
              </View>
            </>
          ) : (
            <>
              {/* Title row with optional back arrow */}
              <View style={styles.headerRow}>
                {showBackButton ? (
                  <TouchableOpacity style={styles.backButton} onPress={onBack} testID="backButton">
                    <Ionicons name="arrow-back" size={22} color="#4b4132" />
                  </TouchableOpacity>
                ) : (
                  <View style={styles.sidePlaceholder} />
                )}

                <Text style={styles.titleCentered}>
                  {i18n.t('camScanner.selectCamera', { defaultValue: 'Select Camera' })}
                </Text>

                <View style={styles.sidePlaceholder} />
              </View>

              <ScrollView style={styles.cameraList}>
                {backCameras.map((camera, index) => {
                  const isSelected = camera.deviceId === selectedCameraId;
                  return (
                    <TouchableOpacity
                      key={camera.deviceId || index}
                      style={[styles.cameraOption, isSelected && styles.cameraOptionSelected]}
                      onPress={() => onSelect(camera.deviceId)}
                      testID={`cameraOption${index}`}
                    >
                      <Ionicons name="camera" size={24} color="#4b4132" />
                      <Text style={styles.cameraLabel}>{getCameraLabel(camera, index)}</Text>
                      {isSelected ? (
                        <Ionicons name="checkmark-circle" size={24} color="#2e7d32" />
                      ) : (
                        <Ionicons name="chevron-forward" size={24} color="#4b4132" />
                      )}
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>

              <TouchableOpacity
                style={styles.cancelButton}
                onPress={onCancel}
                testID="cancelButton"
              >
                <Text style={styles.cancelButtonText}>
                  {i18n.t('camScanner.cancel', { defaultValue: 'Cancel' })}
                </Text>
              </TouchableOpacity>
            </>
          )}
        </LinearGradient>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    width: '85%',
    maxWidth: 400,
    maxHeight: '80%',
    borderRadius: 12,
    padding: 20,
  },

  // ── Shared header row ──
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  title: {
    flex: 1,
    fontSize: 20,
    fontWeight: 'bold',
    color: '#4b4132',
  },
  titleCentered: {
    flex: 1,
    fontSize: 20,
    fontWeight: 'bold',
    color: '#4b4132',
    textAlign: 'center',
  },
  closeIcon: {
    padding: 2,
  },
  backButton: {
    padding: 2,
    width: 30,
  },
  sidePlaceholder: {
    width: 30,
  },

  // ── Step 1 ──
  iconCircle: {
    width: 68,
    height: 68,
    borderRadius: 34,
    backgroundColor: '#4b4132',
    alignSelf: 'center',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 14,
  },
  titleWithIcon: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  instructionTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  instructionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#4b4132',
  },
  instructionBody: {
    fontSize: 14,
    color: '#5a3e1b',
    textAlign: 'center',
    marginBottom: 14,
    lineHeight: 20,
  },
  tipBox: {
    backgroundColor: 'rgba(255,255,255,0.75)',
    borderRadius: 8,
    padding: 12,
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  tipBoxText: {
    flex: 1,
    fontSize: 13,
    color: '#4b4132',
    lineHeight: 20,
  },
  bold: {
    fontWeight: '700',
  },
  tipFooter: {
    fontSize: 12,
    color: '#5a3e1b',
    textAlign: 'center',
    marginBottom: 18,
    lineHeight: 18,
  },
  buttonRow: {
    flexDirection: 'row',
    gap: 10,
  },
  step1CancelButton: {
    flex: 1,
    backgroundColor: '#757575',
    paddingVertical: 13,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  nextButton: {
    flex: 1,
    backgroundColor: '#4b4132',
    paddingVertical: 13,
    borderRadius: 8,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  nextButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },

  // ── Step 2 ──
  cameraList: {
    marginBottom: 16,
  },
  cameraOption: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    padding: 15,
    borderRadius: 8,
    marginBottom: 10,
  },
  cameraOptionSelected: {
    backgroundColor: 'rgba(255, 255, 255, 1)',
    borderWidth: 2,
    borderColor: '#2e7d32',
  },
  cameraLabel: {
    flex: 1,
    fontSize: 16,
    color: '#4b4132',
    marginLeft: 15,
    fontWeight: '500',
  },

  // Shared cancel (step 2 – full width)
  cancelButton: {
    backgroundColor: '#757575',
    paddingVertical: 12,
    borderRadius: 5,
    alignItems: 'center',
  },
  cancelButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
});

export default CameraSelectModal;
